import apiClient from './base/apiClient'
import type { QuizResult, Selections } from '../lib/quizScoring'
import { QUESTIONS, CHIP, type Trait } from '../data/quiz'

/**
 * 취향분석(입맛 유형 테스트) 결과 저장.
 *
 * 웹사이트(tastee.kr)의 src/lib/quizApi.ts 와 **같은 API 를 부르지만 다른
 * 방식**이다. 거기는 비로그인 방문자라 fetch 로 맨몸 요청을 보내고, 여기는
 * apiClient 를 쓴다 — 그래야 로그인 토큰이 함께 가고 서버가 user_id 를
 * 채운다(QuizController@store). 앱 안에서 푼 결과를 내 것으로 남기는 길이
 * 이것뿐이다.
 *
 * 저장이 실패해도 화면은 결과를 보여준다. 테스트를 다시 풀게 만드는 것보다
 * 통계 한 건을 잃는 편이 낫다.
 */

/** 서버가 받는 모양으로 답을 편다 (웹사이트 toAnswerList 와 같은 규칙) */
function toAnswerList(sel: Selections): Array<{ q: string; a: string }> {
  const out: Array<{ q: string; a: string }> = []
  QUESTIONS.forEach((q) => {
    const a = sel[q.id]
    if (a) out.push({ q: q.id, a })
  })
  const chip = sel[CHIP.id]
  if (chip) out.push({ q: CHIP.id, a: chip })
  return out
}

export type QuizEventName =
  | 'start'
  | 'answer'
  | 'complete'
  | 'share_click'
  | 'app_click'
  | 'feedback'

/** 브라우저 한 벌을 가리키는 값. 서버가 피드백 수정 권한을 볼 때 쓴다. */
const SESSION_KEY = 'tastee_quiz_session'

export function getSessionId(): string {
  try {
    const saved = localStorage.getItem(SESSION_KEY)
    if (saved) return saved
    const made = `s_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`
    localStorage.setItem(SESSION_KEY, made)
    return made
  } catch {
    return `s_${Date.now().toString(36)}`
  }
}

export const quizApi = {
  logEvent: (event: QuizEventName, questionId?: string): void => {
    void apiClient
      .post('/quiz/events', {
        session_id: getSessionId(),
        event,
        question_id: questionId ?? null,
      })
      .catch(() => undefined)
  },

  submitResponse: async (
    sel: Selections,
    result: QuizResult,
    retakeOf: string | null,
  ): Promise<string | null> => {
    try {
      const res = await apiClient.post<{ id?: string }>('/quiz/responses', {
        session_id: getSessionId(),
        scoring_version: result.scoringVersion,
        answers: toAnswerList(sel),
        raw_scores: result.raw,
        adj_scores: Object.fromEntries(
          Object.entries(result.adjusted).map(([k, v]) => [
            k,
            Math.round(v * 10000) / 10000,
          ]),
        ),
        primary_trait: result.primary,
        secondary_trait: result.secondary,
        insights: result.insights,
        retake_of: retakeOf,
      })
      return typeof res.data?.id === 'string' ? res.data.id : null
    } catch {
      return null
    }
  },

  sendFeedback: async (
    responseId: string | null,
    feedback: 'accurate' | 'inaccurate',
    selfIdentified?: Trait,
  ): Promise<void> => {
    if (!responseId) return
    try {
      await apiClient.patch(`/quiz/responses/${responseId}/feedback`, {
        session_id: getSessionId(),
        feedback,
        self_identified_trait: selfIdentified ?? null,
      })
    } catch {
      /* 통계용이라 실패해도 화면은 그대로 */
    }
  },
}

export default quizApi
